// ============================================================================
// BANNERUPLOADPANEL.JS - VERSION: 2026.01.11.1200
// ============================================================================
// Drop-in component for uploading banners in the Game Admin Panel
// Supports: Quiz-specific uploads + Shared banner library
// SIMPLIFIED: No collapse, prominent Apply & Save button
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Check, Image, Trash2, Library, Copy, ExternalLink } from 'lucide-react';

const API_URL = 'https://api.traktag.com/api';

// Banner size configurations
const BANNER_TYPES = [
    { key: 'desktop', label: '🖥️ Desktop', size: '728×90', field: 'desktop_banner_url' },
    { key: 'mobile', label: '📱 Mobile', size: '320×50', field: 'mobile_banner_url' },
    { key: 'static', label: '📺 Static', size: '300×250', field: 'static_ad_url' },
    { key: 'exit', label: '🚪 Exit', size: '300×600', field: 'exit_ad_url' },
    { key: 'interstitial', label: '🎬 Interstitial', size: '320×480', field: 'interstitialAdUrl' }
];

// Single banner upload slot
const BannerSlot = ({ type, category, currentUrl, onUpload, onClear }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('File too large (max 5MB)');
            return;
        }

        setError('');
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('banner', file);
            formData.append('category', category);
            formData.append('bannerType', type.key);

            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const result = await response.json();
            
            if (result.success) {
                onUpload(type.field, result.url);
            } else {
                setError(result.error || 'Upload failed');
            }
        } catch (err) {
            setError('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="bg-purple-700 rounded p-2">
            <p className="text-purple-200 text-xs font-semibold">{type.label}</p>
            <p className="text-purple-400 text-xs mb-1">{type.size}</p>

            {currentUrl ? (
                <div className="relative mb-1">
                    <img 
                        src={currentUrl} 
                        alt={type.label}
                        className="w-full h-10 object-contain bg-purple-900 rounded"
                    />
                    <span className="absolute top-0 right-0 bg-green-600 text-white text-xs px-1 rounded-bl">✓</span>
                </div>
            ) : (
                <div className="w-full h-10 bg-purple-800 rounded flex items-center justify-center mb-1">
                    <span className="text-purple-500 text-xs">Empty</span>
                </div>
            )}

            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
            />
            
            <div className="flex gap-1">
                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className={`flex-1 text-xs py-1 rounded ${uploading ? 'bg-gray-600' : 'bg-purple-600 hover:bg-purple-500'} text-white`}
                >
                    {uploading ? '⏳' : <><Upload size={10} className="inline mr-1" />{currentUrl ? 'Replace' : 'Upload'}</>}
                </button>
                {currentUrl && (
                    <button
                        onClick={() => onClear(type.field)}
                        className="bg-red-600 hover:bg-red-500 text-white text-xs px-2 rounded"
                    >
                        <Trash2 size={10} />
                    </button>
                )}
            </div>
            {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
        </div>
    );
};

// Main panel component
const BannerUploadPanel = ({ category, gameData, onUpdateField, affiliateUrl, onUpdateAffiliateUrl, onSave }) => {
    const [sharedSponsors, setSharedSponsors] = useState([]);
    const [loadingShared, setLoadingShared] = useState(true);
    const [selectedSponsor, setSelectedSponsor] = useState(null);
    const [applying, setApplying] = useState(false);

    const categorySlug = (category || 'general').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    useEffect(() => {
        loadSharedSponsors();
    }, []);

    const loadSharedSponsors = async () => {
        setLoadingShared(true);
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setSharedSponsors(data.sponsors || []);
        } catch (err) {
            console.error('Failed to load shared sponsors:', err);
        } finally {
            setLoadingShared(false);
        }
    };

    const handleUpload = (field, url) => {
        onUpdateField(field, url);
    };

    const handleClear = (field) => {
        onUpdateField(field, '');
    };

    // Apply all banners from a sponsor AND save
    const applyAndSave = async (sponsor) => {
        setApplying(true);
        
        // Apply all banner URLs
        BANNER_TYPES.forEach(type => {
            if (sponsor.banners[type.key]) {
                onUpdateField(type.field, sponsor.banners[type.key]);
            }
        });

        // Apply affiliate URL if set
        if (sponsor.affiliateUrl) {
            onUpdateAffiliateUrl(sponsor.affiliateUrl);
        }

        setSelectedSponsor(sponsor.sponsor);

        // Small delay to ensure state updates, then save
        setTimeout(() => {
            if (onSave) {
                onSave();
            }
            setApplying(false);
        }, 100);
    };

    // Count how many banners a sponsor has
    const countBanners = (sponsor) => {
        return BANNER_TYPES.filter(t => sponsor.banners?.[t.key]).length;
    };

    return (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-white font-bold flex items-center gap-2">
                    <Image size={18} /> 📺 Banner Ads
                </h4>
                <span className="text-purple-400 text-xs">Quiz: {categorySlug}</span>
            </div>

            {/* === SHARED BANNER LIBRARY - ALWAYS VISIBLE === */}
            <div className="bg-purple-900 rounded-lg p-4">
                <h5 className="text-teal-300 font-bold flex items-center gap-2 mb-3">
                    <Library size={18} /> 📚 Apply from Shared Banner Library
                </h5>
                
                {loadingShared ? (
                    <p className="text-purple-400">Loading sponsors...</p>
                ) : sharedSponsors.length === 0 ? (
                    <p className="text-purple-400 text-sm">
                        No shared banners yet. Go to <strong>Shared Banners</strong> screen to upload sponsor banners.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {sharedSponsors.map(sponsor => (
                            <div 
                                key={sponsor.sponsor}
                                className={`bg-purple-800 rounded-lg p-3 border-2 ${
                                    selectedSponsor === sponsor.sponsor 
                                        ? 'border-teal-400' 
                                        : 'border-purple-600'
                                }`}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <span className="text-white font-bold text-lg">
                                            {sponsor.displayName || sponsor.sponsor}
                                        </span>
                                        <span className="text-purple-400 text-sm ml-2">
                                            ({countBanners(sponsor)}/5 banners)
                                        </span>
                                        {sponsor.affiliateUrl && (
                                            <a 
                                                href={sponsor.affiliateUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-teal-400 text-xs ml-2 hover:underline"
                                            >
                                                <ExternalLink size={12} className="inline" /> Link
                                            </a>
                                        )}
                                    </div>
                                    
                                    <button
                                        onClick={() => applyAndSave(sponsor)}
                                        disabled={applying || countBanners(sponsor) === 0}
                                        className={`px-4 py-2 rounded-lg font-bold text-white flex items-center gap-2 ${
                                            applying 
                                                ? 'bg-gray-600' 
                                                : countBanners(sponsor) === 0
                                                    ? 'bg-gray-600 cursor-not-allowed'
                                                    : 'bg-green-600 hover:bg-green-500'
                                        }`}
                                    >
                                        {applying ? (
                                            '⏳ Applying...'
                                        ) : (
                                            <>
                                                <Check size={16} /> Apply & Save
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* Show banner preview icons */}
                                <div className="flex gap-2 mt-2">
                                    {BANNER_TYPES.map(type => (
                                        <div 
                                            key={type.key}
                                            className={`text-xs px-2 py-1 rounded ${
                                                sponsor.banners?.[type.key]
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-purple-700 text-purple-400'
                                            }`}
                                            title={sponsor.banners?.[type.key] ? `${type.label} available` : `${type.label} missing`}
                                        >
                                            {type.label.split(' ')[0]}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    onClick={loadSharedSponsors}
                    className="text-purple-400 hover:text-purple-300 text-xs mt-2"
                >
                    🔄 Refresh list
                </button>
            </div>

            {/* === AFFILIATE URL === */}
            <div className="bg-purple-900 rounded-lg p-3">
                <label className="text-teal-300 text-sm font-semibold block mb-2">
                    🔗 Affiliate Click URL (where banners link to)
                </label>
                <input
                    type="url"
                    value={affiliateUrl || ''}
                    onChange={(e) => onUpdateAffiliateUrl(e.target.value)}
                    placeholder="https://go.nordvpn.net/aff_c?offer_id=15&aff_id=YOUR_ID"
                    className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
                />
            </div>

            {/* === CURRENT BANNER STATUS === */}
            <div className="bg-purple-900 rounded-lg p-3">
                <p className="text-purple-300 text-sm font-semibold mb-2">📊 Current Banner Status:</p>
                <div className="grid grid-cols-5 gap-2">
                    {BANNER_TYPES.map(type => {
                        const hasUrl = gameData?.[type.field];
                        return (
                            <div 
                                key={type.key}
                                className={`text-center p-2 rounded ${hasUrl ? 'bg-green-600' : 'bg-purple-700'}`}
                            >
                                <p className="text-white text-xs font-bold">{type.label.split(' ')[0]}</p>
                                <p className="text-xs">{hasUrl ? '✓' : '—'}</p>
                            </div>
                        );
                    })}
                </div>
                {selectedSponsor && (
                    <p className="text-teal-400 text-xs mt-2">
                        ✓ Using banners from: <strong>{selectedSponsor}</strong>
                    </p>
                )}
            </div>

            {/* === MANUAL UPLOAD (COLLAPSED) === */}
            <details className="bg-purple-900 rounded-lg p-3">
                <summary className="text-purple-300 text-sm cursor-pointer hover:text-purple-200">
                    📤 Manual Upload (click to expand)
                </summary>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                    {BANNER_TYPES.map(type => (
                        <BannerSlot
                            key={type.key}
                            type={type}
                            category={categorySlug}
                            currentUrl={gameData?.[type.field] || ''}
                            onUpload={handleUpload}
                            onClear={handleClear}
                        />
                    ))}
                </div>
            </details>
        </div>
    );
};

export default BannerUploadPanel;
